import React, {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import { Button, Input, Modal, Text, Grid, Row, Col, Divider } from 'rsuite';
import {
  performSignInAsync,
  submitAuthCode,
} from '../../store/slices/auth/authThunks';
import { useSelector } from 'react-redux';
import {
  selectIsSendAuthCodeInProgress,
  selectIsSubmitAuthCodeInProgress,
  selectWorkErrorMessage,
  selectWorkFailed,
} from '../../store/slices/auth/authSelectors';
import IconIndicator from '../user-feedback/IconIndicator';
import Notice from '../user-feedback/Notice';

const AuthCodeModal = ({
  onModalConfirm = () => null,
  onModalReject = () => null,
  showModal,
  usage = 'login',
}) => {
  const dispatch = useDispatch();
  const [code, setCode] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const triggerRef = useRef(null);

  useLayoutEffect(() => {
    if (showModal) {
      triggerRef.current = document.activeElement;
    }
  }, [showModal]);

  useEffect(() => {
    if (!showModal && triggerRef.current) {
      const trigger = triggerRef.current;
      triggerRef.current = null;
      trigger?.focus();
    }
  }, [showModal]);

  const sendingAuthCodeEmail = useSelector(selectIsSendAuthCodeInProgress);
  const loginFailed = useSelector(selectWorkFailed);
  const loginErrorMsg = useSelector(selectWorkErrorMessage);
  const sumbittingAuthCodeInProgress = useSelector(
    selectIsSubmitAuthCodeInProgress
  );

  useEffect(() => {
    setModalOpen(showModal);
  }, [showModal]);

  const isAuthorizingAccountCreation = useMemo(() => {
    return usage === 'create_account';
  }, [usage]);

  const onSubmitAuthCode = () => {
    dispatch(submitAuthCode(code, isAuthorizingAccountCreation));
  };

  const handleModalReject = () => {
    setCode('');
    onModalReject();
  };

  const handleModalConfirm = () => {
    onSubmitAuthCode();
    setCode('');
    onModalConfirm();
  };

  const resendCode = () => {
    dispatch(performSignInAsync(isAuthorizingAccountCreation));
  };

  return (
    <Modal
      backdrop={true}
      keyboard={false}
      open={modalOpen}
      onClose={handleModalReject}
      dialogClassName={'no-padding-modal'}
    >
      <Modal.Header>
        <Modal.Title
          as="h2"
          className="text-bold no-padding-modal-section"
        >
          Verification
        </Modal.Title>
        <Divider />
      </Modal.Header>
      <Modal.Body>
        <Grid
          fluid
          className="no-padding-modal-section"
        >
          <Row>
            <Col
              xsHidden
              sm={1}
            ></Col>
            <Col
              xs={24}
              sm={16}
            >
              <div role="grid">
                <Row className="mb-16">
                  <Col xs={24}>
                    <Text>
                      We have sent a verification code to the email provided.
                      Please enter the code below. Make sure to check your spam
                      folder.
                    </Text>
                  </Col>
                </Row>
                <Row className="mb-16">
                  <Col
                    sm={12}
                    xs={24}
                    md={10}
                  >
                    <Input
                      aria-label="Code Input"
                      onChange={setCode}
                      onPressEnter={handleModalConfirm}
                      value={code}
                      autoComplete="off"
                    />
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Button
                      disabled={sendingAuthCodeEmail}
                      className="link-color"
                      onClick={() => resendCode()}
                    >
                      Resend code
                    </Button>
                  </Col>
                </Row>
              </div>
            </Col>
            <Col
              xsHidden
              xs={24}
              sm={6}
            >
              <img
                src="\assets\email-verification.svg"
                alt="Verification icon"
                className="full-width"
              />
            </Col>
          </Row>
        </Grid>
      </Modal.Body>
      <Modal.Footer>
        <Divider />
        <Grid
          fluid
          className="no-padding-modal-section"
        >
          {(sendingAuthCodeEmail || sumbittingAuthCodeInProgress) && (
            <Row className="mb-8">
              <Col xs={24}>
                <IconIndicator />
              </Col>
            </Row>
          )}
          {loginFailed &&
            !sendingAuthCodeEmail &&
            !sumbittingAuthCodeInProgress && (
              <Row className="mb-16">
                <Col xs={24}>
                  <Notice
                    message={loginErrorMsg ?? 'Unknown error occurred'}
                    iconSize={16}
                    messageStyle={{ fontSize: 12 }}
                  />
                </Col>
              </Row>
            )}
          <Row>
            <Col xs={24}>
              <Button
                onClick={handleModalConfirm}
                className="btn-primary"
                disabled={sendingAuthCodeEmail || sumbittingAuthCodeInProgress}
                data-cy="auth-code-submit-button"
              >
                Submit
              </Button>
              <Button
                onClick={handleModalReject}
                className="btn-secondary"
                data-cy="auth-code-cancel-button"
              >
                Cancel
              </Button>
            </Col>
          </Row>
        </Grid>
      </Modal.Footer>
    </Modal>
  );
};

export default AuthCodeModal;
